import * as React from 'react';
import { useLocation } from 'react-router-dom';
import { FacilityType, InspectionPointUsage } from '../../amorg-common';
import { getFacilityTypes } from '../../api/locationClientApi';
import { getInspectionPointUsage } from '../../api/safetyInspectionClientApi';
import { HelpModalToolBarButton } from '../../designSystem/compositeComponents/HelpModalToolbBarButton';
import { SingleColumnLayout } from '../_layouts/SingleColumnLayout';
import { PageHeader } from '../../designSystem/PageHeader';
import { P } from '../../designSystem/typography/Body';
import { clientAccessHelper } from '../../utils/clientAccessHelper';
import { ProtectedPageContext } from '../_contexts/ProtectedPageContext';
import { MemberPageWrapper } from '../_pageWrappers/MemberPageWrapper';
import { InspectionPointConfiguration } from './_components/InspectionPointConfiguration';

type State = {
    facilityTypes:FacilityType[],
    inspectionPointUsage:InspectionPointUsage[]
}
export default function InspectionPointConfigurationPage(): JSX.Element {

    const urlQuerySegment = useLocation().search;
    const urlParams = new URLSearchParams(urlQuerySegment);
    const safetyInspectionId = urlParams.get('safetyInspectionId');

    return (
        <MemberPageWrapper
            allowUser={true}
            breadCrumbs={[
                safetyInspectionId 
                    ?   { 
                            text: 'Tilbage til sikkerhedsrunderingen...', 
                            url: `/safetyinspections/${safetyInspectionId}`
                        }
                    :   { 
                            text: 'Tilbage til oversigten...', 
                            url: `/safetyinspections`
                        },
                { text: 'Kontrolpunkter', url: '' }
            ]}>
            <InspectionPointConfigurationPageContent />
        </MemberPageWrapper>
    );
}

function InspectionPointConfigurationPageContent(): JSX.Element
{
    const activeUser = React.useContext(ProtectedPageContext).user;
    const hasAccess = clientAccessHelper.hasAllPermissions(activeUser, ['ViewAssignedActionPlans']);

    const [state, setState] = React.useState<State>({
        facilityTypes:[],
        inspectionPointUsage:[]
    });

    React.useEffect(() => {
        handleUpdate();
    }, []);

    async function handleUpdate()
    {
        const inspectionPointUsage = (await getInspectionPointUsage())
            .sort((a,b) => a.title.localeCompare(b.title));

        const facilityTypes = await getFacilityTypes();

        setState({
            facilityTypes,
            inspectionPointUsage
        });
    }

    if (!hasAccess)
        return <P>Hov! Du har ikke rettigheder til at tilgå denne side.</P>;

    return (
        <>
            <PageHeader 
                narrow
                headerText="Kontrolpunkter" 
                buttons={
                    <HelpModalToolBarButton title='Hjælp til kontrolpunkter' helpContent={<HelpContents />} />
                } />

            <SingleColumnLayout narrow>
                <>
                    <InspectionPointConfiguration inspectionPointUsage={state.inspectionPointUsage} allFacilityTypes={state.facilityTypes} onUpdated={handleUpdate} />
                </>
            </SingleColumnLayout>
        </>
    );
}

function HelpContents():JSX.Element
{
    return (
        <section style={{display:'flex', flexDirection:'column', gap:'10px'}}>
            <P>
                Når en facilitet tilføjes til en sikkerhedsrundering, afhænger de oprettede kontrolpunkter af facilitetens type. På den måde kan f.eks et køkken og en garderobe have forskellige kontrolpunkter.
            </P>

            <P>
                Hvilke kontrolpunkter der hører til en facilitetstype kan frit ændres, ligesom nye kontrolpunkter kan tilføjes og eksisterende kan fjernes. <b>Bemærk dog at ændringeringerne først træder i kraft når en facilitet tilføjes til en ny sikkerhedsrundering</b>.
                På den måde kan det undgås, at ændre i afviklede sikkerhedsrunderinger, hvilket ville være problematisk i forhold til dokumentation og historik.
            </P>

            <P>
                Det er heller ikke muligt at ændre på titel og instruktion for et kontrolpunkt hvis det allerede er i brug i en sikkerhedsrundering.
            </P>
        </section>
    );
}